import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, CheckCircle, Package, Pencil, Printer, Loader2, MapPin, AlertTriangle, Flag, Download, Truck, Store, FileText } from "lucide-react";
import Barcode from "react-barcode";
import CollectionLabel from "./collection-label";
import { playSuccess, playError, playComplete } from "../helpers/audioPlayer";
import { logoutProcessor } from "../helpers/logoutProcessor";
import { useTranslation } from "../../i18n";
import { toast } from "react-toastify";
import { silentPrint, browserPrint, buildPrintDocument, getSavedLocalPrinter, PRINT_AGENT_DOWNLOAD_URL, resolvePrintAgentDownloadUrl } from "../helpers/printAgent";
import { submitNetworkPrintJob, getSavedNetworkPrinter, detectPrintMode } from "../helpers/networkPrint";
import { printPdfBase64, printPdfsBase64, printZplWithCascade, blobToBase64, base64ToZpl } from "../helpers/carrierLabelPrint";
import DispatchPrinterButton from "./dispatch-printer-button";
import type { DeskInfo, DispatchOrder, DispatchBox, CourierBookingResult } from "../../types/Dispatch";
import { measuredContentsGrams } from "./ownPackaging";
import { apiFetch } from "../../api/apiFetch";
import { logDispatchEvent } from "./dispatchLog";
import { useReadOnly } from "../../access/ReadOnly";
import DispatchOrderBar from "./dispatch-order-bar";
import { DispatchSessionStats } from "./dispatch-session-chrome";
import "../../styles/dispatch-bench.css";

type Props = {
    order: DispatchOrder;
    courierResult: CourierBookingResult;
    deskInfo: DeskInfo;
    boxes: DispatchBox[];
    autoPrint?: boolean;
    onShipComplete: () => void;
    /** Desk session: awaited before the ship request so the saved desk is never behind it. */
    onBeforeShip?: () => Promise<void>;
    /**
     * Skip this screen's own green "ORDER SHIPPED" overlay (and its
     * completion chime) and hand straight over to onShipComplete.
     *
     * Set by the flagged-order resolve flow, which shows its OWN
     * completion screen once the resolution has persisted. Without this
     * the leader sat through two full-screen green overlays back to back
     * — "Order Shipped", then "Order Resolved" — and heard the chime
     * twice. The resolve screen names the shipment, so nothing is lost.
     */
    suppressSuccessOverlay?: boolean;
    onFlagOrder?: () => void;
    goBack: () => void;
    /**
     * Flagged Picks: tell the BE this ship is a leader's decision to go
     * without a unit still being fetched from high racking. Only the
     * flagged-order resolve flow sets it; the pack desk never ships short
     * past a live flagged pick (BE 409 flagged_pick_pending).
     */
    shipShort?: boolean;
};

const DEMO_MODE = true; // Set to false when API is live

const getCurrentUser = (): { uid: string; name: string } => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return { uid: "", name: "" };
        const u = JSON.parse(raw);
        return { uid: u?.uid ?? "", name: u?.name ?? "" };
    } catch { return { uid: "", name: "" }; }
};

const DispatchCloseOrder = ({ order, courierResult, deskInfo, boxes, autoPrint, onShipComplete, onBeforeShip, suppressSuccessOverlay, onFlagOrder, goBack, shipShort }: Props) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);

    // Printer detection — same cascade as the wipe module: a saved network
    // printer (print agent installed on ANY warehouse PC) wins, then the
    // local agent on this machine, then nothing (browser dialog fallback +
    // install prompt). undefined = detection still running, null = none found.
    const [printMode, setPrintMode] = useState<"local" | "network" | null | undefined>(undefined);
    // Auto-print fires on mount and races the async detection, so the print
    // path awaits this promise instead of reading printMode state.
    const printModeRef = useRef<Promise<"local" | "network" | null> | null>(null);

    const detectPrinter = useCallback(() => {
        const detection = detectPrintMode();
        printModeRef.current = detection;
        detection.then(setPrintMode);
        return detection;
    }, []);
    useEffect(() => { detectPrinter(); }, [detectPrinter]);
    // Installer link — staging FE (newest build) when reachable, else this origin.
    const [downloadUrl, setDownloadUrl] = useState(PRINT_AGENT_DOWNLOAD_URL);
    useEffect(() => { resolvePrintAgentDownloadUrl().then(setDownloadUrl); }, []);
    const [fetchingLabel, setFetchingLabel] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [editingAddress, setEditingAddress] = useState(false);
    const [address, setAddress] = useState({ ...order.delivery_address });
    const [printError, setPrintError] = useState<string | null>(null);
    // Which step the banner is about. The fulfilment write can be refused
    // AFTER every label has printed (a tracking-number clash, the carrier
    // guard), and a banner headed "Print failed" for that sent leaders
    // reprinting a label that was fine — order 345240 (2026-09-09) printed
    // its six labels four times over chasing a ship refusal.
    const [printErrorStep, setPrintErrorStep] = useState<"print" | "ship">("print");
    const printRef = useRef<HTMLDivElement>(null);
    // Synchronous in-flight lock for the print+ship flow — see
    // handlePrintAndShip. Must be a ref, not state: the whole point is that
    // it is observable by a second caller in the SAME render pass.
    const printInFlight = useRef(false);

    const isMultiBox = boxes.length > 1;

    // Order-level shipping method. Its badge sits in the order bar (shared
    // with the pack screen); here it decides the collection flow.
    const deliveryType = (order.delivery_type ?? "").toLowerCase();
    // Collection order (delivery_type = collection): handed over at the
    // warehouse counter. No courier was booked (the pack screen hands over a
    // collection result), this screen prints a COLLECTION label with the
    // order-number barcode instead of a courier label, and the close carries
    // collection:true so the BE records the parcels as carrier 'Collection'.
    const isCollection = deliveryType === "collection" || courierResult.collection === true;

    // Auto-print: fire immediately after layout so printRef is available
    const hasAutoTriggered = useRef(false);
    useLayoutEffect(() => {
        if (autoPrint && !hasAutoTriggered.current) {
            hasAutoTriggered.current = true;
            handlePrintAndShip();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    // Carrier-booking print path (APC): fetch the stored base64 ZPL from
    // the booking row and print it verbatim on the desk's Zebra —
    // network agent first, then local agent, then the PDF fallback for
    // non-Zebra printers / pre-1.6.0 agents (printZplWithCascade /
    // printPdfBase64 in helpers/carrierLabelPrint — shared with the
    // Orders-page reprint so both take the same route). Returns true
    // when every parcel's label printed.
    const printCarrierLabels = useCallback(async (bookingId: number): Promise<{ success: boolean; error?: string }> => {
        const fetchPieces = async (format: "ZPL" | "PDF") => {
            const res = await apiFetch(`/carriers/bookings/${bookingId}/labels`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ format }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.status !== "label_ready") {
                throw new Error(data.error ?? data.message ?? t("label_fetch_failed"));
            }
            return (data.pieces ?? []) as { piece: number; tracking_number: string; content_base64: string; format?: "ZPL" | "PDF" }[];
        };

        try {
            const pieces = await fetchPieces("ZPL");
            const mode = await (printModeRef.current ?? detectPrinter());
            let zplOk = true;
            let lastError: string | undefined;
            // A PDF-only carrier (Cargus) answers a ZPL request with PDF
            // pieces and says so per piece. Those never go near the ZPL
            // cascade — sending PDF bytes to a Zebra prints garbage.
            if (pieces.length > 0 && pieces.every((p) => p.format === "PDF")) {
                const ok = await printPdfsBase64(pieces.map((p) => p.content_base64));
                return ok ? { success: true } : { success: false, error: t("print_failed") };
            }
            // Which pieces physically came out of the printer already. The
            // ZPL loop below breaks on the FIRST failure, so on a partial
            // failure some labels are already on the bench — and the PDF
            // fallback used to reprint the WHOLE order, handing the packer a
            // second copy of everything printed before the break. Tracking
            // numbers, not indexes: the PDF fetch is a separate call and its
            // ordering is not guaranteed to match.
            const printedTracking = new Set<string>();
            for (const piece of pieces) {
                if (piece.format === "PDF") {
                    const ok = await printPdfBase64(piece.content_base64);
                    if (!ok) { zplOk = false; lastError = t("print_failed"); break; }
                    printedTracking.add(piece.tracking_number);
                    continue;
                }
                const r = await printZplWithCascade(base64ToZpl(piece.content_base64), mode);
                if (!r.success) { zplOk = false; lastError = r.error; break; }
                printedTracking.add(piece.tracking_number);
            }
            if (zplOk) return { success: true };

            // ZPL route unavailable (non-Zebra desk, old agent) → PDF, but
            // ONLY for the pieces that never made it out above — in one
            // print dialog (printPdfsBase64), or the browser drops all but
            // the first.
            const pdfPieces = await fetchPieces("PDF");
            const ok = await printPdfsBase64(
                pdfPieces
                    .filter((piece) => !printedTracking.has(piece.tracking_number))
                    .map((piece) => piece.content_base64),
            );
            return ok ? { success: true } : { success: false, error: lastError ?? t("print_failed") };
        } catch (err) {
            return { success: false, error: err instanceof Error ? err.message : t("print_failed") };
        }
    }, [detectPrinter, t]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch label from courier API, then print, then ship
    const handlePrintAndShip = useCallback(async () => {
        // In-flight lock. This guard USED to read `loading || printing ||
        // fetchingLabel`, which are React state captured in this closure —
        // setFetchingLabel(true) doesn't land until the next render, so two
        // invocations inside one render pass BOTH saw false and BOTH ran the
        // whole print. Auto-print firing alongside an operator tap is exactly
        // that, and it printed every label of the order twice: booking 387
        // fetched its 4 labels twice 194ms apart, booking 385 its 3 twice
        // (2026-08-20). A ref flips synchronously, so the second caller
        // returns immediately.
        if (printInFlight.current) return;
        printInFlight.current = true;
        try {
            await runPrintAndShip();
        } finally {
            printInFlight.current = false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, printing, fetchingLabel, courierResult, order, deskInfo, address, t]);

    const runPrintAndShip = useCallback(async () => {
        if (loading || printing || fetchingLabel) return;
        setPrintError(null);
        setPrintErrorStep("print");

        // ── Own-label shipment: no courier was booked. Print the uploaded
        //    shipping label and ship. Nothing was charged. ──
        //
        //    Zebra route first: the BE renders the PDF to ZPL (the Cargus
        //    path, pdfLabel/convertPdfLabels.js) and it goes out through the
        //    print agent like a carrier label — silently, so auto-print on
        //    these orders no longer stops at the browser's PDF dialog
        //    (2026-10-28). The PDF dialog stays as the fallback: no Zebra
        //    route on this desk, the BE could not render it, or an older BE
        //    without the endpoint.
        if (courierResult.own_label && courierResult.own_label_attachment_id) {
            setFetchingLabel(true);
            setPrinting(true);
            let ok = false;
            let errMsg: string | null = null;
            let route: "zpl" | "pdf" = "pdf";
            let mode: "local" | "network" | null = null;
            let zplError: string | null = null;
            const attachmentBase = `/orders/${encodeURIComponent(order.order_id)}/attachments/${encodeURIComponent(courierResult.own_label_attachment_id)}`;
            try {
                let pdfBase64: string | null = null;
                mode = await (printModeRef.current ?? detectPrinter());
                // Always try the Zebra route, whatever detection said — the
                // same as printCarrierLabels. A null mode only means the
                // /status probe failed at mount (4 s timeout, agent still
                // starting); the cascade still tries the local agent, and
                // with no agent at all it refuses instantly and we go to the
                // dialog. Gating on the mode sent desks whose APC labels print
                // silently to the dialog for own-label orders (Pembroke,
                // 2026-10-29).
                const labelRes = await apiFetch(`${attachmentBase}/label`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ format: "ZPL" }),
                }).catch(() => null);
                const data = labelRes && labelRes.ok
                    ? await labelRes.json().catch(() => null) as { status?: string; format?: string; content_base64?: string } | null
                    : null;
                if (data?.status === "label_ready" && data.content_base64) {
                    if (data.format === "ZPL") {
                        const r = await printZplWithCascade(base64ToZpl(data.content_base64), mode);
                        if (r.success) { ok = true; route = "zpl"; }
                        else { zplError = r.error ?? "zpl-failed"; errMsg = r.error ?? null; }
                    } else {
                        // The BE could not render it and sent the PDF.
                        zplError = "be-sent-pdf";
                        pdfBase64 = data.content_base64;
                    }
                } else {
                    zplError = labelRes ? `label-endpoint-${labelRes.status}` : "label-endpoint-unreachable";
                }
                if (!ok) {
                    if (!pdfBase64) {
                        const res = await apiFetch(`${attachmentBase}/download`, {});
                        if (!res.ok) throw new Error(t("attached_label_fetch_failed"));
                        const blob = await res.blob();
                        // printPdfBase64 wants raw base64 (no data: prefix).
                        pdfBase64 = await blobToBase64(blob).catch(() => {
                            throw new Error(t("attached_label_fetch_failed"));
                        });
                    }
                    ok = await printPdfBase64(pdfBase64);
                    errMsg = ok ? null : (errMsg ?? t("print_failed"));
                }
            } catch (err) {
                ok = false;
                errMsg = err instanceof Error ? err.message : t("attached_label_fetch_failed");
            }
            setFetchingLabel(false);
            setPrinting(false);
            logDispatchEvent({
                order_pick_type: order.pick_type,
                event: "label-print",
                order_id: order.order_id,
                desk_id: deskInfo.deskId,
                carrier: "own_label",
                route,
                // Why a desk ended up at the dialog: what detection saw and
                // where the Zebra route gave up.
                print_mode: mode ?? "none",
                ...(zplError ? { zpl_error: zplError.slice(0, 80) } : {}),
                result: ok ? "ok" : `fail-${errMsg ?? "print"}`.slice(0, 80),
            });
            if (ok) {
                doShip();
            } else {
                playError();
                setPrintError(errMsg || t("print_failed"));
            }
            return;
        }

        // ── Carrier bookings (APC): real label from the booking row ──
        if (courierResult.booking_id) {
            setFetchingLabel(true);
            setPrinting(true);
            const result = await printCarrierLabels(courierResult.booking_id);
            setFetchingLabel(false);
            setPrinting(false);
            logDispatchEvent({
                order_pick_type: order.pick_type,
                event: "label-print",
                order_id: order.order_id,
                desk_id: deskInfo.deskId,
                carrier: (courierResult.carrier || "apc").toLowerCase(),
                result: result.success ? "ok" : `fail-${result.error ?? "print"}`.slice(0, 80),
            });
            if (result.success) {
                doShip();
            } else {
                playError();
                setPrintError(result.error || t("print_failed"));
            }
            return;
        }

        setFetchingLabel(true);

        // ── Step 1: Request label(s) from courier API ──
        let labelHtml: string | null = null;

        // Collection orders ALWAYS take the locally-rendered route: the
        // hidden print area holds the COLLECTION label (order-number
        // barcode) and there is no courier API to ask.
        if (isCollection || DEMO_MODE) {
            // Demo: use the locally-rendered label
            // Wait a frame for react-barcode SVGs to render into the hidden div
            await new Promise(r => requestAnimationFrame(r));
            if (printRef.current) {
                labelHtml = buildPrintDocument(printRef.current.innerHTML);
            }
        } else {
            try {
                const res = await apiFetch(`/api/v1/dispatch/labels`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        order_id: order.order_id,
                        delivery_address: address,
                        parcels: courierResult.parcels.map(p => ({
                            carrier: p.carrier,
                            tracking_number: p.tracking_number,
                            service: p.service,
                        })),
                    }),
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data.label_html) {
                        labelHtml = data.label_html;
                    } else if (data.label_url) {
                        // If API returns a URL to the label, fetch it
                        const labelRes = await fetch(data.label_url);
                        labelHtml = await labelRes.text();
                    } else {
                        throw new Error(data.error?.message || t("label_fetch_failed"));
                    }
                } else if (res.status === 401) {
                    logoutProcessor();
                    navigate("/login");
                    setFetchingLabel(false);
                    return;
                } else {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(data.error?.message || data.message || `${t("label_fetch_failed")} (${res.status})`);
                }
            } catch (err: any) {
                setFetchingLabel(false);
                playError();
                setPrintError(err.message || t("label_fetch_failed"));
                return;
            }
        }

        setFetchingLabel(false);

        if (!labelHtml) {
            playError();
            setPrintError(t("label_fetch_failed"));
            return;
        }

        // ── Step 2: Print — network printer, then local agent, then browser ──
        setPrinting(true);

        // Gate the network path on the detected mode (not just the saved
        // selection) so choosing "Local printer" / disconnecting in the modal
        // is honoured even while a network selection stays in localStorage.
        const mode = await (printModeRef.current ?? detectPrinter());
        const saved = getSavedNetworkPrinter();
        let result: { success: boolean; error?: string } = { success: false };
        if (mode === "network" && saved) {
            result = await submitNetworkPrintJob(saved.agentId, saved.printerName, labelHtml, 1);
        }
        if (!result.success) {
            // Local agent — honour the specific local printer picked in the
            // modal; without one the agent uses the Windows default.
            result = await silentPrint(labelHtml, 1, getSavedLocalPrinter() ?? undefined);
        }
        if (!result.success) {
            // Agent unreachable or errored — fall back to the browser's native
            // print dialog which sends to the OS default printer.
            result = await browserPrint(labelHtml);
        }
        setPrinting(false);

        logDispatchEvent({
            order_pick_type: order.pick_type,
            event: "label-print",
            order_id: order.order_id,
            desk_id: deskInfo.deskId,
            result: result.success ? "ok" : `fail-${result.error ?? "print"}`.slice(0, 80),
        });

        if (result.success) {
            doShip();
        } else {
            playError();
            setPrintError(result.error || t("print_failed"));
        }
    }, [loading, printing]); // eslint-disable-line react-hooks/exhaustive-deps

    // Declare the shipment done. Normally that means the full-screen green
    // "ORDER SHIPPED" overlay for 800ms, then hand back to the caller.
    // In resolveMode the caller owns the completion screen (see
    // suppressSuccessOverlay), so we hand over immediately and stay silent
    // rather than stacking two green overlays and two chimes.
    const finishShip = useCallback(() => {
        if (suppressSuccessOverlay) {
            setLoading(false);
            onShipComplete();
            return;
        }
        playComplete();
        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            onShipComplete();
        }, 800);
    }, [suppressSuccessOverlay, onShipComplete]);

    const doShip = useCallback(async () => {
        if (loading || readOnly) return;
        setLoading(true);
        try { await onBeforeShip?.(); } catch { /* saving is best effort */ }

        // Build the parcel payload for the portal-BE order_label table.
        // One row per parcel from the courier-book result, joined with the
        // matching DispatchBox so we can persist the cardboard used (box
        // barcode + dimensions + tare weight).
        //
        // Per-parcel items come from the packer's ACTUAL box assignments
        // (assignedBoxes carries one entry per shipped unit), not from the
        // courier response — courier bookings don't echo a reliable
        // per-parcel split (the demo generator used to dump every item on
        // parcel 1 and record the rest as empty).
        const boxItemsFor = (boxId: number | undefined) =>
            boxId == null ? [] : order.items
                .map(i => ({ sku: i.sku, quantity: i.assignedBoxes.filter(b => b === boxId).length }))
                .filter(x => x.quantity > 0);
        const portalParcels = courierResult.parcels.map((parcel, idx) => {
            const box = boxes[idx];
            const items = boxItemsFor(box?.id);
            // An own-packaging container weighed at the bench records the
            // reading, the same figure the booking declared.
            const contentsWeight = measuredContentsGrams(box) ?? items.reduce((sum, pi) => {
                const orderItem = order.items.find(i => i.sku === pi.sku);
                return sum + (orderItem?.weight_grams ?? 0) * pi.quantity;
            }, 0);
            return {
                tracking_number: parcel.tracking_number,
                carrier: parcel.carrier,
                service: parcel.service ?? "",
                tracking_url: parcel.tracking_url ?? null,
                box_position: idx + 1,
                box_barcode: box?.boxRef ?? null,
                box_name: box?.boxName ?? null,
                box_length_mm: box?.boxLengthMm ?? null,
                box_width_mm: box?.boxWidthMm ?? null,
                box_height_mm: box?.boxHeightMm ?? null,
                box_weight_grams: box?.boxWeightGrams ?? null,
                contents_weight_grams: contentsWeight,
                items,
            };
        });
        // Pack-desk resolution counts, persisted alongside the labels so
        // the admin order view can explain a partial shipment ("1 unit
        // marked damaged") — previously these lived only in FE session
        // state and were lost when the desk closed the order.
        const itemCounts = order.items
            .filter(i => i.line_position != null)
            .map(i => ({
                line_position: i.line_position,
                scanned: i.scannedCount || 0,
                missing: i.missingCount || 0,
                damaged: i.damagedCount || 0,
                wrong_item: i.wrongItemCount || 0,
                barcode_damaged: i.barcodeDamagedCount || 0,
            }));
        const user = getCurrentUser();
        // Persist the parcels and flip orders.status → 'fulfilled' in
        // portal-BE. Returns whether the write actually landed.
        //   • Non-demo path: the legacy WMS /ship call below is the courier
        //     source-of-truth and this is a status mirror, so it stays
        //     fire-and-forget there.
        //   • Demo path: this is the ONLY status write, so the caller MUST
        //     confirm it succeeded before declaring the order shipped —
        //     otherwise a dropped/500'd write leaves the order 'picked', the
        //     tote re-surfaces on the next scan, and the order can be
        //     "shipped" again despite the operator seeing a success screen.
        const recordLabels = async (): Promise<{ ok: boolean; message?: string }> => {
            try {
                const res = await apiFetch(
                    `/orders/${encodeURIComponent(order.order_id)}/labels`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            parcels: portalParcels,
                            item_counts: itemCounts,
                            packed_by_uid: user.uid,
                            packed_by_name: user.name,
                            desk_id: deskInfo.deskId,
                            // Exempts the fulfilment from the carrier-booking
                            // guard and lets the BE synthesise tracking for the
                            // trackingless own-label parcels.
                            own_label: courierResult.own_label === true,
                            // Collection order: exempt from the carrier guard
                            // too; the BE pins carrier 'Collection' and
                            // synthesises the tracking numbers.
                            collection: isCollection,
                            ship_short: shipShort === true,
                        }),
                    },
                );
                if (res.ok) return { ok: true };
                // The BE names the refusal (parcel_tracking_conflict,
                // carrier_booking_required…). Pass its words on: a bare
                // "Shipment failed" hid the cause from the leader AND from
                // whoever they asked.
                const data = await res.json().catch(() => ({}));
                const message = typeof data?.message === "string" && data.message.trim()
                    ? data.message.trim()
                    : undefined;
                return { ok: false, message };
            } catch {
                return { ok: false };
            }
        };

        // ── Demo mode: simulate success ──
        if (DEMO_MODE) {
            const recorded = await recordLabels();
            if (!recorded.ok) {
                // Fulfilment write didn't land — don't tell the operator the
                // order shipped (it's still 'picked' and would re-surface on
                // the next tote scan). Surface the error so they can retry.
                setLoading(false);
                playError();
                setPrintErrorStep("ship");
                // A named refusal is not a "try again" — show it as-is.
                setPrintError(recorded.message ?? t("ship_failed"));
                return;
            }
            finishShip();
            setLoading(false);
            return;
        }
        // ── End demo mode ──

        try {
            // Build parcels payload
            const parcelsPayload = courierResult.parcels.map(parcel => ({
                carrier: parcel.carrier,
                tracking_number: parcel.tracking_number,
                tracking_url: parcel.tracking_url,
                service: parcel.service,
                items: parcel.items,
            }));

            const res = await apiFetch(`/api/v1/orders/${order.order_id}/ship`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    parcels: parcelsPayload,
                    shipped_at: new Date().toISOString(),
                    warehouse_id: deskInfo.warehouse,
                    delivery_address: address,
                }),
            });

            if (res.ok) {
                // Mirror the labels into our portal-BE so order_label /
                // order_label_items reflect what just shipped. Fire-and-
                // forget — the legacy WMS write above is the source of truth
                // for the courier integration; ours is for status tracking.
                recordLabels();
                // Build refund lines per-unit from the item counts so partial
                // rows (e.g. 2 shipped + 1 damaged) refund only the flagged units.
                const refundLines: { item_id: string; quantity: number; reason: string }[] = [];
                for (const item of order.items) {
                    if (item.missingCount > 0) {
                        refundLines.push({ item_id: item.item_id, quantity: item.missingCount, reason: "not_received" });
                    }
                    if (item.damagedCount > 0) {
                        refundLines.push({ item_id: item.item_id, quantity: item.damagedCount, reason: "damaged" });
                    }
                    if (item.wrongItemCount > 0) {
                        refundLines.push({ item_id: item.item_id, quantity: item.wrongItemCount, reason: "wrong_item" });
                    }
                }

                if (refundLines.length > 0) {
                    const flaggedSkus = order.items
                        .filter(i => i.missingCount > 0 || i.damagedCount > 0)
                        .map(i => i.sku);
                    try {
                        await apiFetch(`/api/v1/orders/${order.order_id}/refund`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                type: "partial",
                                items: refundLines,
                                reason: "damaged",
                                internal_note: `Auto-refund from dispatch. Desk: ${deskInfo.deskId}. Items: ${flaggedSkus.join(", ")}`,
                                refund_delivery: false,
                            }),
                        });
                    } catch {
                        // Refund failure is non-blocking — will be handled end of day
                    }
                }

                finishShip();
            } else if (res.status === 401) {
                logoutProcessor();
                navigate("/login");
            } else {
                playError();
                toast.dismiss();
                toast.error(t("ship_failed"));
            }
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("ship_failed"));
        }

        setLoading(false);
    }, [loading, order, courierResult, deskInfo, finishShip, navigate, t, onBeforeShip]);

    // Ctrl+S keyboard shortcut
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === "s") {
                e.preventDefault();
                handlePrintAndShip();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [doShip]);

    // Success overlay
    if (showSuccess) {
        return (
            <div className="dispatch-ship-success-overlay">
                <CheckCircle strokeWidth={3} />
                <h1>{t("order_shipped")}</h1>
            </div>
        );
    }

    // Order bar counts: units shipping, units shipping short, and the
    // shipped weight — the same figures the pack screen showed.
    const totalUnits = order.items.reduce((sum, i) => sum + i.quantity, 0);
    const shippedUnits = order.items.reduce((sum, i) => sum + (i.scannedCount || 0) + (i.barcodeDamagedCount || 0), 0);
    const shortUnits = order.items.reduce(
        (sum, i) => sum + (i.missingCount || 0) + (i.damagedCount || 0) + (i.wrongItemCount || 0),
        0,
    );
    const shippedWeightG = order.items.reduce(
        (sum, i) => sum + i.weight_grams * ((i.scannedCount || 0) + (i.barcodeDamagedCount || 0)),
        0,
    );
    const shortItems = order.items.filter(i => i.missingCount > 0 || i.damagedCount > 0 || i.wrongItemCount > 0);

    // Which label is about to print, so the parcel goes in the right cage.
    // The booking already carried all of this; the screen never showed it.
    const firstParcel = courierResult.parcels[0];
    const carrierName = isCollection
        ? t("delivery_type_collection")
        : courierResult.own_label
            ? t("own_label_shipment")
            : (courierResult.carrier || firstParcel?.carrier || "");
    const carrierService = isCollection || courierResult.own_label
        ? ""
        : (courierResult.service || firstParcel?.service || "");
    const carrierRef = isCollection
        ? ""
        : courierResult.own_label
            ? (courierResult.own_label_filename ?? "")
            : (courierResult.waybill || firstParcel?.tracking_number || "");
    const labelCount = isCollection || courierResult.own_label
        ? boxes.length
        : (courierResult.parcels.length || boxes.length);
    const CarrierIcon = isCollection ? Store : courierResult.own_label ? FileText : Truck;

    // Layout (2026-10-23 bench redesign): the pack screen's order bar on top,
    // boxes on the left and the address + anything shipping short on the
    // right, and Print fixed bottom-right — the spot Complete holds on the
    // pack screen. Print used to sit under everything else, so a 3-box
    // short-shipped order pushed it off the bottom of the 768px screens.
    return (
        <div className="dispatch-page dispatch-bench dispatch-confirm">
            <DispatchOrderBar
                order={order}
                done={shippedUnits}
                short={shortUnits}
                total={totalUnits}
                weightKg={shippedWeightG / 1000}
                actions={(
                    <>
                        {onFlagOrder && (
                            <button
                                type="button"
                                className="dispatch-bar-btn dispatch-flag-btn"
                                onClick={onFlagOrder}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                aria-label={t("flag_order")}
                                title={readOnly ? t("read_only_mode") : t("flag_order")}
                            >
                                <Flag size={16} aria-hidden="true" />
                                {t("dispatch_flag_short")}
                            </button>
                        )}
                        <button type="button" className="dispatch-bar-icon-btn" onClick={goBack} aria-label={t("back")}>
                            <ArrowLeft size={20} />
                        </button>
                    </>
                )}
            />

            {/* Collection order: handed over at the counter. Loud, so nobody
                carries the parcel to the courier cage — the label that prints
                is a COLLECTION label, not a courier label. */}
            {isCollection && (
                <div className="dispatch-notice dispatch-notice--collection dispatch-collection-note" role="status">
                    <Store size={20} aria-hidden="true" />
                    <span>{t("collection_order_banner")}</span>
                </div>
            )}

            {/* Add-box-to-existing-consignment: the boxes were added onto an
                existing same-day consignment for this customer, so ONLY the
                new box label(s) were issued — flag it so the packer isn't
                surprised by fewer labels than a standalone order. */}
            {courierResult?.merged && courierResult.waybill && (
                <div className="dispatch-notice dispatch-notice--info dispatch-merged-note">
                    {t("dispatch_added_to_consignment", { waybill: courierResult.waybill })}
                </div>
            )}

            <div className="dispatch-confirm-body">
                <div className="dispatch-confirm-col">
                    <div className="dispatch-carrier-card">
                        <div className="dispatch-carrier-card-icon">
                            <CarrierIcon size={22} aria-hidden="true" />
                        </div>
                        <div className="dispatch-carrier-card-info">
                            <span className="dispatch-carrier-card-name">{carrierName}</span>
                            {(carrierService || carrierRef) && (
                                <span className="dispatch-carrier-card-sub">
                                    {carrierService}
                                    {carrierService && carrierRef && " · "}
                                    {carrierRef && <span className="dispatch-carrier-card-ref">{carrierRef}</span>}
                                </span>
                            )}
                        </div>
                        <div className="dispatch-stat">
                            <span className="dispatch-stat-label">{t("dispatch_labels_count")}</span>
                            <span className="dispatch-stat-value dispatch-stat-value--count">{labelCount}</span>
                        </div>
                    </div>

                    {/* Boxes summary — items weight + cardboard tare = total
                        per box, with the box's admin-defined name (or the
                        custom dimensions) when one was scanned. */}
                    <div className="dispatch-ship-summary">
                        {boxes.map(box => {
                            const boxItems = order.items.filter(i => i.assignedBoxes.includes(box.id) && i.scanStatus === "scanned");
                            const itemWeightG = measuredContentsGrams(box) ?? boxItems.reduce(
                                (sum, i) => sum + (i.weight_grams * i.assignedBoxes.filter(b => b === box.id).length),
                                0,
                            );
                            const tareG = box.boxWeightGrams ?? 0;
                            const totalG = itemWeightG + tareG;
                            const dimsCm = (box.boxLengthMm && box.boxWidthMm && box.boxHeightMm)
                                ? `${Math.round(box.boxLengthMm / 10)}×${Math.round(box.boxWidthMm / 10)}×${Math.round(box.boxHeightMm / 10)} cm`
                                : null;
                            // Real DB box (boxRef set) → its admin-given name
                            // plus dimensions. Custom box (boxRef null but dims
                            // set) → the explicit dimensions, since "Custom"
                            // alone tells the operator nothing useful here.
                            const sizeLabel = box.boxRef
                                ? [box.boxName, dimsCm].filter(Boolean).join(" · ")
                                : dimsCm;
                            const unitsInBox = boxItems.reduce(
                                (sum, i) => sum + i.assignedBoxes.filter(b => b === box.id).length,
                                0,
                            );
                            return (
                                <div key={box.id} className="dispatch-confirm-box dispatch-ship-parcel">
                                    <div className="dispatch-confirm-box-icon">
                                        <Package size={20} aria-hidden="true" />
                                    </div>
                                    <div className="dispatch-confirm-box-info">
                                        <span className="dispatch-confirm-box-name">{t("box_label")} {box.id}</span>
                                        {sizeLabel && <span className="dispatch-confirm-box-size">{sizeLabel}</span>}
                                    </div>
                                    <span className="dispatch-confirm-box-units">
                                        {t("dispatch_box_units", { count: String(unitsInBox) })}
                                    </span>
                                    <span className="dispatch-confirm-box-weight">{(totalG / 1000).toFixed(2)} kg</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="dispatch-confirm-col">
                    {/* Print error banner */}
                    {printError && (
                        <div className="dispatch-print-error">
                            <AlertTriangle size={20} style={{ flexShrink: 0 }} />
                            <div>
                                <p className="dispatch-print-error-title">
                                    {printErrorStep === "ship" ? t("ship_failed_title") : t("print_failed")}
                                </p>
                                <p className="dispatch-print-error-detail">{printError}</p>
                            </div>
                        </div>
                    )}

                    {/* Delivery address — shown, so the packer can check it
                        against the parcel; editing is its own button (opening
                        the old collapsed card dropped straight into the form). */}
                    <div className="dispatch-confirm-address dispatch-address-card">
                        <div className="dispatch-confirm-address-head">
                            <MapPin size={14} aria-hidden="true" />
                            <span className="dispatch-confirm-address-label">{t("delivery_address")}</span>
                            <button
                                type="button"
                                className="dispatch-bar-btn"
                                onClick={() => setEditingAddress(editing => !editing)}
                                aria-pressed={editingAddress}
                            >
                                {editingAddress ? <Check size={14} aria-hidden="true" /> : <Pencil size={14} aria-hidden="true" />}
                                {editingAddress ? t("dispatch_address_done") : t("edit")}
                            </button>
                        </div>
                        {editingAddress ? (
                            <div className="dispatch-address-form">
                                <input value={address.name} onChange={e => setAddress({ ...address, name: e.target.value })} placeholder={t("name")} />
                                <input value={address.line_1} onChange={e => setAddress({ ...address, line_1: e.target.value })} placeholder={t("address_line_1")} />
                                <input value={address.line_2 || ""} onChange={e => setAddress({ ...address, line_2: e.target.value })} placeholder={t("address_line_2")} />
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} placeholder={t("city")} style={{ flex: 1 }} />
                                    <input value={address.county || ""} onChange={e => setAddress({ ...address, county: e.target.value })} placeholder={t("county")} style={{ flex: 1 }} />
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input value={address.postcode} onChange={e => setAddress({ ...address, postcode: e.target.value })} placeholder={t("postcode")} style={{ flex: 1 }} />
                                    <input value={address.country} onChange={e => setAddress({ ...address, country: e.target.value })} placeholder={t("country")} style={{ width: 80 }} />
                                </div>
                                <input value={address.phone || ""} onChange={e => setAddress({ ...address, phone: e.target.value })} placeholder={t("phone")} type="tel" />
                                <input value={address.email || ""} onChange={e => setAddress({ ...address, email: e.target.value })} placeholder={t("email")} type="email" />
                            </div>
                        ) : (
                            <div className="dispatch-address-text">
                                <strong>{address.name}</strong><br />
                                {address.line_1}<br />
                                {address.line_2 && <>{address.line_2}<br /></>}
                                {address.city}{address.county ? `, ${address.county}` : ""}<br />
                                <strong>{address.postcode}</strong> {address.country}
                                {address.phone && <><br />{address.phone}</>}
                                {address.email && <><br />{address.email}</>}
                            </div>
                        )}
                    </div>

                    {/* Shipping short — names the product and how many, not
                        just the SKU. */}
                    {shortItems.length > 0 && (
                        <div className="dispatch-confirm-short">
                            <div className="dispatch-confirm-short-head">
                                <AlertTriangle size={18} aria-hidden="true" />
                                {t("items_flagged")}
                            </div>
                            {shortItems.map(i => (
                                <div key={i.item_id} className="dispatch-confirm-short-line">
                                    <div className="dispatch-confirm-short-row">
                                        {i.missingCount > 0 && (
                                            <span className="dispatch-confirm-short-chip">
                                                {t("dispatch_item_missing_chip", { count: String(i.missingCount) })}
                                            </span>
                                        )}
                                        {i.damagedCount > 0 && (
                                            <span className="dispatch-confirm-short-chip">
                                                {t("dispatch_item_damaged_chip", { count: String(i.damagedCount) })}
                                            </span>
                                        )}
                                        {i.wrongItemCount > 0 && (
                                            <span className="dispatch-confirm-short-chip">
                                                {t("dispatch_item_wrong_chip", { count: String(i.wrongItemCount) })}
                                            </span>
                                        )}
                                        <span className="dispatch-confirm-short-title">{i.title || i.sku}</span>
                                    </div>
                                    <span className="dispatch-confirm-short-sku">{i.sku}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom bar: printer, session counts and Print. */}
            <div className="dispatch-confirm-footer">
                {/* Printer picker — the SHARED pill, the same component the
                    dispatch scan screens use, so the affordance is identical
                    everywhere — and on the Flagged resolve route, which has
                    no scan screens, this is the ONLY printer control a leader
                    ever sees. onModeChange re-detects so a printer changed
                    here is the one printModeRef uses for the actual print. */}
                <DispatchPrinterButton onModeChange={detectPrinter} />
                {printMode === null && !/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) && (
                    <a href={downloadUrl} className="dispatch-bar-btn dispatch-confirm-agent-link">
                        <Download size={16} aria-hidden="true" />
                        {t("download_print_agent")}
                    </a>
                )}
                <DispatchSessionStats />
                <div className="dispatch-confirm-footer-spacer" />
                <button
                    type="button"
                    className="btn btn-success dispatch-confirm-print"
                    onClick={handlePrintAndShip}
                    disabled={loading || printing || fetchingLabel}
                >
                    {(fetchingLabel || printing) ? (
                        <Loader2 size={24} className="dispatch-spin" />
                    ) : isCollection ? (
                        <Store size={24} />
                    ) : courierResult.own_label ? (
                        <FileText size={24} />
                    ) : (
                        <Printer size={24} />
                    )}
                    {fetchingLabel
                        ? t("fetching_label")
                        : printing
                            ? t("printing")
                            : isCollection
                                ? t("print_collection_label")
                                : courierResult.own_label
                                    ? t("print_attached_label")
                                    : isMultiBox ? t("print_labels") : t("print_label")}
                </button>
            </div>

            {/* Hidden print area — shipping labels, one per box. A collection
                order renders COLLECTION labels here instead (order-number
                barcode, no address, no carrier). */}
            <div style={{ position: "absolute", left: "-9999px", top: 0, visibility: "hidden" }}>
                <div ref={printRef}>
                    {isCollection ? boxes.map((box, idx) => (
                        <CollectionLabel
                            key={box.id}
                            orderNumber={order.order_number}
                            customerName={address.name || order.customer?.name || ""}
                            boxIndex={idx + 1}
                            boxCount={boxes.length}
                            warehouse={deskInfo.warehouse}
                        />
                    )) : boxes.map((box, idx) => {
                        const boxItems = order.items.filter(i => i.assignedBoxes.includes(box.id) && i.scanStatus === "scanned");
                        const boxWeight = boxItems.reduce((sum, i) => sum + (i.weight_grams * i.assignedBoxes.filter(b => b === box.id).length), 0);
                        const boxQty = boxItems.reduce((sum, i) => sum + i.assignedBoxes.filter(b => b === box.id).length, 0);
                        const parcel = courierResult.parcels[idx] || courierResult.parcels[0];
                        const tracking = parcel?.tracking_number || `TRK${Date.now()}${idx}`;
                        return (
                            <div key={box.id} className="dispatch-print-label">
                                {/* Carrier banner */}
                                <div className="dpl-carrier-banner">
                                    <span className="dpl-carrier-name">{parcel?.carrier || "YODEL"}</span>
                                    <span className="dpl-carrier-service">{parcel?.service || "STANDARD"}</span>
                                </div>

                                {/* Main tracking barcode */}
                                <div className="dpl-tracking-barcode">
                                    <Barcode value={tracking} height={40} width={1.5} fontSize={10} margin={0} displayValue={true} />
                                </div>

                                {/* Address section */}
                                <div className="dpl-address-section">
                                    <div className="dpl-address-from">
                                        <p className="dpl-address-label">{t("from_label")}</p>
                                        <p><strong>JLI Trading Limited</strong></p>
                                        <p>Unit 4, Maesglas Industrial Estate</p>
                                        <p>Newport, NP20 2NN</p>
                                        <p>United Kingdom</p>
                                    </div>
                                    <div className="dpl-address-to">
                                        <p className="dpl-address-label">{t("deliver_to_label")}</p>
                                        <p><strong style={{ fontSize: "16px" }}>{address.name}</strong></p>
                                        <p>{address.line_1}</p>
                                        {address.line_2 && <p>{address.line_2}</p>}
                                        <p>{address.city}{address.county ? `, ${address.county}` : ""}</p>
                                        <p style={{ fontSize: "16px", fontWeight: 900, letterSpacing: "0.05em", marginTop: 2 }}>{address.postcode}</p>
                                        <p>{address.country}</p>
                                    </div>
                                </div>

                                {/* Order info bar */}
                                <div className="dpl-info-bar">
                                    <div className="dpl-info-cell">
                                        <span className="dpl-info-label">{t("order_title_label")}</span>
                                        <span className="dpl-info-value">{order.order_number}</span>
                                    </div>
                                    <div className="dpl-info-cell">
                                        <span className="dpl-info-label">{t("box_label")}</span>
                                        <span className="dpl-info-value">{box.id} / {boxes.length}</span>
                                    </div>
                                    <div className="dpl-info-cell">
                                        <span className="dpl-info-label">{t("weight_label")}</span>
                                        <span className="dpl-info-value">{(boxWeight / 1000).toFixed(2)} kg</span>
                                    </div>
                                    <div className="dpl-info-cell">
                                        <span className="dpl-info-label">{t("item_plural")}</span>
                                        <span className="dpl-info-value">{boxQty}</span>
                                    </div>
                                </div>

                                {/* Item list */}
                                <div className="dpl-items">
                                    {boxItems.map(item => (
                                        <span key={item.item_id} className="dpl-item">
                                            {item.sku} x{item.assignedBoxes.filter(b => b === box.id).length}
                                        </span>
                                    ))}
                                </div>

                                {/* Sort code barcode (postcode-based) */}
                                <div className="dpl-sort-barcode">
                                    <Barcode value={address.postcode.replace(/\s/g, "")} height={25} width={1.5} fontSize={9} margin={0} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>


            {/* Block all interaction while printing/fetching */}
            {(fetchingLabel || printing || loading) && (
                <div className="dispatch-print-lock" />
            )}

        </div>
    );
};

export default DispatchCloseOrder;
